import {
  FileArchive,
  FileAudio,
  FileCode,
  FileImage,
  FileJson,
  FileSpreadsheet,
  FileText,
  FileType,
  FileVideo,
  Film,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "../../utils";
import { ProviderIcon } from "../ProviderIcon";

import { FileName, ImportPrefix } from "./constants";

enum FileIconProvider {
  Openclaw = "openclaw",
  Claude = "claude",
  Codex = "codex",
}

export interface FileIconProps {
  path: string;
  className?: string;
}

const EXTENSION_ICON_MAP: Record<string, LucideIcon> = {
  json: FileJson,
  jsonl: FileJson,

  yaml: FileCode,
  yml: FileCode,
  toml: FileCode,
  xml: FileCode,

  md: FileText,
  mdx: FileText,
  markdown: FileText,
  txt: FileText,
  log: FileText,
  rtf: FileText,
  pdf: FileText,

  js: FileCode,
  jsx: FileCode,
  mjs: FileCode,
  cjs: FileCode,
  ts: FileCode,
  tsx: FileCode,
  py: FileCode,
  rb: FileCode,
  go: FileCode,
  rs: FileCode,
  java: FileCode,
  kt: FileCode,
  swift: FileCode,
  c: FileCode,
  cpp: FileCode,
  h: FileCode,
  hpp: FileCode,
  cs: FileCode,
  php: FileCode,
  sh: FileCode,
  bash: FileCode,
  zsh: FileCode,
  fish: FileCode,
  ps1: FileCode,
  sql: FileCode,
  html: FileCode,
  htm: FileCode,
  css: FileCode,
  scss: FileCode,
  sass: FileCode,
  less: FileCode,

  csv: FileSpreadsheet,
  tsv: FileSpreadsheet,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,

  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  gif: FileImage,
  webp: FileImage,
  bmp: FileImage,
  ico: FileImage,
  svg: FileImage,
  avif: FileImage,
  heic: FileImage,

  mp3: FileAudio,
  wav: FileAudio,
  ogg: FileAudio,
  flac: FileAudio,
  m4a: FileAudio,

  mp4: FileVideo,
  mov: FileVideo,
  webm: FileVideo,
  mkv: FileVideo,
  avi: Film,

  zip: FileArchive,
  tar: FileArchive,
  gz: FileArchive,
  tgz: FileArchive,
  rar: FileArchive,
  "7z": FileArchive,

  ttf: FileType,
  otf: FileType,
  woff: FileType,
  woff2: FileType,

  env: FileCode,
  ini: FileCode,
  conf: FileCode,
  lock: FileCode,
};

const getIconForExtension = (ext: string): LucideIcon => EXTENSION_ICON_MAP[ext] ?? FileText;

export const FileIcon = ({ path, className }: FileIconProps) => {
  const name = path.split("/").pop()?.toLowerCase() ?? "";
  const ext = name.split(".").pop() ?? "";
  const normalizedPath = path.replace(/\\/g, "/").toLowerCase();
  const openclawAgentsPath = `${ImportPrefix.Openclaw}${FileName.AgentsMd}`.toLowerCase();

  const renderProviderIcon = (provider: FileIconProvider) => (
    <div className={cn("flex h-4 w-4 shrink-0 items-center justify-center", className)}>
      <ProviderIcon provider={provider} className="h-4 w-4 shrink-0" />
    </div>
  );

  if (
    name === FileName.AgentsMd.toLowerCase() &&
    (normalizedPath === openclawAgentsPath ||
      normalizedPath.endsWith(`/${openclawAgentsPath}`) ||
      normalizedPath.startsWith(ImportPrefix.Openclaw))
  ) {
    return renderProviderIcon(FileIconProvider.Openclaw);
  }

  switch (name) {
    case FileName.ClaudeMd.toLowerCase():
      return renderProviderIcon(FileIconProvider.Claude);
    case FileName.AgentsMd.toLowerCase():
      return renderProviderIcon(FileIconProvider.Codex);
    case FileName.SkillMd.toLowerCase():
      return <FileText className={cn("h-4 w-4 shrink-0 text-gray-500", className)} />;
    default:
      break;
  }

  const Icon = getIconForExtension(ext);
  return <Icon className={cn("h-4 w-4 shrink-0 text-gray-500", className)} />;
};
